import fishipediaManifest from "../data/fishipediaManifest.json";

function normalizeFishipediaHashValue(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeFishipediaSearchValue(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function compactFishipediaValue(value) {
  return normalizeFishipediaHashValue(value).replace(/-/g, "");
}

const FISHIPEDIA_ALIAS_TO_SLUG = {
  albacore: "albacore-longfin-tuna",
  amberjack: "greater-amberjack",
  barracuda: "european-barracuda",
  "common-dolphinfish": "mahi-mahi",
  "cyprinus-carpio": "common-carp",
  dentex: "common-dentex",
  "esox-lucius": "northern-pike",
  "european-sea-bass": "european-seabass",
  "false-albacore": "false-albacore-little-tunny",
  "flathead-grey-mullet": "grey-mullet",
  "little-tunny": "false-albacore-little-tunny",
  "oncorhynchus-mykiss": "rainbow-trout",
  "perca-fluviatilis": "european-perch",
  "saddle-bream": "saddled-seabream",
  "salmo-trutta": "brown-trout",
  "sander-lucioperca": "zander",
  "sea-bass": "european-seabass",
  seabass: "european-seabass",
  "silurus-glanis": "wels-catfish",
  "squalius-cephalus": "european-chub",
  "squelius-cephalus": "european-chub",
  "yellowstripe-barracuda": "european-barracuda",
};

function buildFishipediaLookup() {
  const byHash = new Map();
  const bySearch = new Map();
  const byCompact = new Map();

  function register(value, entry) {
    const normalizedHash = normalizeFishipediaHashValue(value);
    const normalizedSearch = normalizeFishipediaSearchValue(value);
    const compact = compactFishipediaValue(value);

    if (normalizedHash && !byHash.has(normalizedHash)) {
      byHash.set(normalizedHash, entry);
    }

    if (normalizedSearch && !bySearch.has(normalizedSearch)) {
      bySearch.set(normalizedSearch, entry);
    }

    if (compact && !byCompact.has(compact)) {
      byCompact.set(compact, entry);
    }
  }

  fishipediaManifest.forEach((entry) => {
    register(entry.slug, entry);
    register(entry.anchorId, entry);
    register(entry.label, entry);
  });

  return { byHash, bySearch, byCompact };
}

const FISHIPEDIA_LOOKUP = buildFishipediaLookup();

function findFishipediaEntryByValue(value) {
  const normalizedHash = normalizeFishipediaHashValue(value);
  const normalizedSearch = normalizeFishipediaSearchValue(value);
  const compact = compactFishipediaValue(value);
  const aliasSlug = FISHIPEDIA_ALIAS_TO_SLUG[normalizedHash] || null;
  const compactAliasSlug = FISHIPEDIA_ALIAS_TO_SLUG[compact] || null;

  return (
    FISHIPEDIA_LOOKUP.byHash.get(normalizedHash) ||
    FISHIPEDIA_LOOKUP.byCompact.get(compact) ||
    FISHIPEDIA_LOOKUP.bySearch.get(normalizedSearch) ||
    (aliasSlug ? FISHIPEDIA_LOOKUP.byHash.get(aliasSlug) : null) ||
    (compactAliasSlug ? FISHIPEDIA_LOOKUP.byHash.get(compactAliasSlug) : null) ||
    null
  );
}

function collectFishipediaCandidates(input) {
  const candidates = [];

  function pushCandidate(value) {
    if (typeof value !== "string") {
      return;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    if (!candidates.includes(trimmed)) {
      candidates.push(trimmed);
    }
  }

  if (typeof input === "object" && input !== null) {
    pushCandidate(input.id);
    pushCandidate(input.name);
    pushCandidate(input.commonName);
    pushCandidate(input.common_name);
    pushCandidate(input.scientificName);
    pushCandidate(input.displayName);

    if (Array.isArray(input.aliases)) {
      input.aliases.forEach(pushCandidate);
    }
  } else {
    pushCandidate(input);
  }

  candidates.forEach((candidate) => {
    const match = candidate.match(/^(.+?)\s*\(([^)]+)\)$/);
    if (!match) {
      if (candidate.includes("/")) {
        candidate.split(/\s*\/\s*/).forEach(pushCandidate);
      }
      return;
    }

    pushCandidate(match[1]);
    pushCandidate(match[2]);

    if (match[1].includes("/")) {
      match[1].split(/\s*\/\s*/).forEach(pushCandidate);
    }
  });

  return candidates;
}

export function resolveFishipediaEntry(input) {
  const candidates = collectFishipediaCandidates(input);

  for (const candidate of candidates) {
    const entry = findFishipediaEntryByValue(candidate);
    if (entry) {
      return entry;
    }
  }

  return null;
}
